// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { Incident, Meta } from './types';
import { speciesInfo, activityLabel, stateName } from './domain';

export interface Dataset {
  meta: Meta;
  incidents: Incident[];
  byId: Map<number, Incident>;
}

async function fetchJson<T>(url: string): Promise<T> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`Failed to load ${url}: ${r.status}`);
  return r.json() as Promise<T>;
}

export async function loadDataset(): Promise<Dataset> {
  const [meta, incidents] = await Promise.all([
    fetchJson<Meta>('data/meta.json'),
    fetchJson<Incident[]>('data/incidents.json'),
  ]);
  const byId = new Map(incidents.map((i) => [i.id, i]));
  return { meta, incidents, byId };
}

let geoCache: unknown | null = null;
export async function loadGeo(): Promise<unknown> {
  if (geoCache) return geoCache;
  geoCache = await fetchJson('data/boundaries.geojson');
  return geoCache;
}

// ── Header search over incidents (location / species / state) ──────────────────
export interface SearchHit {
  incident: Incident;
  label: string;
  sub: string;
}

export function searchIncidents(incidents: Incident[], q: string, limit = 12): SearchHit[] {
  const query = q.trim().toLowerCase();
  if (query.length < 2) return [];
  const scored: Array<{ hit: SearchHit; score: number }> = [];
  for (const it of incidents) {
    const loc = it.loc.toLowerCase();
    const sp = speciesInfo(it.sp).label.toLowerCase();
    let score = 0;
    if (loc.startsWith(query)) score = 3;
    else if (loc.includes(query)) score = 2;
    else if (sp.includes(query)) score = 1;
    else if (it.st && stateName(it.st).toLowerCase().includes(query)) score = 1;
    if (!score) continue;
    // Prefer recent, well-described incidents.
    const recency = (it.y ?? 1800) / 3000;
    scored.push({
      hit: {
        incident: it,
        label: it.loc ? titleLoc(it.loc) : `${stateName(it.st ?? '')} (location unknown)`,
        sub: `${it.y ?? '—'} · ${speciesInfo(it.sp).label} · ${activityLabel(it.act)}`,
      },
      score: score + recency,
    });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => s.hit);
}

function titleLoc(s: string): string {
  return s.replace(/\b([a-z])/g, (m) => m.toUpperCase());
}
